import _ from 'lodash'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { Container } from '@mui/material'

//components
import Layout from 'components/layout/Games'
import SlideLayout from 'components/layout/Games/Slide'
import RewardHistory from 'components/rewardHistory'
import Paginator from 'components/Pagination'
import BackgroundPigsAnimation from 'components/BackgroundPigsAnimation'
import Items from './components/Items'
import NoItems from './components/NoItems'
import DailyStreakView from 'pages/GamesList/components/DailyStreakView'
import PiggyBankChest from 'pages/GamesList/components/Alerts/PiggyBankChest'
import AggregatedReward from 'pages/GamesList/components/Alerts/AggregatedReward'
//constants
import {
  PAGINATION_LIMIT,
  LOCAL_STORAGE,
  REWARD_HISTORY_TYPE,
  ONBOARDING_STEP,
} from 'constants/index'
//contexts
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
import HistoryContext from 'context/HistoryContext'
import NativeAppContext from 'context/NativeApp/NativeAppContext'
//hooks
import useQueryGetAllUserQuests from 'hooks/useQueryGetAllUserQuests'
import useQueryGetUserQuestsHistory from 'hooks/useQueryGetUserQuestsHistory'
import useLocalStorage from 'hooks/useLocalStorage'
import usePiggyBankChestConfig from 'hooks/usePiggyBankChestConfig'
//services
import { getAllUnclaimedRewards } from 'services/userRewardsService'
//styles
import './index.css'
//types
import { UserQuest } from 'types'
//utils
import { handleError, openGameLink } from 'utils/utils'
import { checkDailyStreakVisibility } from 'utils/dailyStreak'

const PREV_ROUTES = ['/games', '/library', '/rewards']

export enum QUEST_TYPE {
  COMPLETED = 'completed',
  STARTER = 'starter',
  FLASH = 'flash',
  WEEKLY = 'weekly',
}

const QuestsList = () => {
  const { user } = useContext(AuthContext)
  const { previousPage } = useContext(HistoryContext)
  const { nativeAppDetails } = useContext(NativeAppContext)

  const [history, setHistory] = useState<Array<UserQuest>>([])
  const [showBackgroundPigsAnimation, setShowBackgroundPigsAnimation] = useState(false)

  const { getItem, removeItem } = useLocalStorage()
  const { isLoading, showLoader, hideLoader } = useContext(LoaderContext)

  const paginationParams = {
    limit: PAGINATION_LIMIT,
    cursor: history?.length ? history[history.length - 1].id : '',
  }

  const { userQuests, isLoadingQuests } = useQueryGetAllUserQuests()
  const { questsHistory, isLoadingQuestsHistory, refetchHistory } =
    useQueryGetUserQuestsHistory(paginationParams)

  const { piggyChestConfig, isPiggyChestLoading } = usePiggyBankChestConfig()
  const { data: allUnclaimedRewards, isLoading: isLoadingUnclaimedRewards } = useQuery(
    'allUnclaimedRewards',
    getAllUnclaimedRewards,
    { onError: handleError }
  )

  const isFetching =
    isLoadingQuests || isLoadingQuestsHistory || isPiggyChestLoading || isLoadingUnclaimedRewards

  const isOnboardingComplete =
    !user?.onboardingStep || user?.onboardingStep === ONBOARDING_STEP.COMPLETE

  const checkDSVisibility = useCallback(
    () => checkDailyStreakVisibility(Number(nativeAppDetails?.versionCode)),
    [nativeAppDetails?.versionCode]
  )

  const questsFound = useMemo(() => {
    return Object.values(QUEST_TYPE).some((value) => userQuests && userQuests[value]?.length)
  }, [userQuests])

  useEffect(() => {
    if (questsHistory?.histories) {
      setHistory((prev) => _.unionBy(prev, questsHistory.histories, 'id'))
    }
  }, [questsHistory])

  useEffect(() => {
    let mmp = null

    const onboardingMMP = getItem(LOCAL_STORAGE.ONBOARDING_MMP)
    if (onboardingMMP) {
      mmp = JSON.parse(onboardingMMP)
    }

    if (mmp) {
      removeItem(LOCAL_STORAGE.ONBOARDING_MMP)
      openGameLink(mmp.mmpLink, user, mmp.gameDetails)
    }
  }, [])

  useEffect(() => {
    isFetching ? showLoader() : hideLoader()
  }, [isFetching])

  return (
    <Layout isLoading={isLoading}>
      {showBackgroundPigsAnimation && <BackgroundPigsAnimation />}
      <SlideLayout
        prevRoutes={PREV_ROUTES}
        direction={previousPage === PREV_ROUTES[0] ? 'left' : 'right'}
        isSwiping={false}
      >
        <Container maxWidth="sm" sx={styles.container}>
          {user && isOnboardingComplete && (
            <Container maxWidth="sm">
              {checkDSVisibility() && (
                <DailyStreakView setShowBackgroundPigsAnimation={setShowBackgroundPigsAnimation} />
              )}
              {user.isChestAvailableForPurchase && piggyChestConfig && (
                <PiggyBankChest
                  config={piggyChestConfig}
                  setShowBackgroundPigsAnimation={setShowBackgroundPigsAnimation}
                />
              )}
              {allUnclaimedRewards?.rewards?.length > 0 && (
                <AggregatedReward
                  rewards={allUnclaimedRewards.rewards}
                  aggregatedCoins={allUnclaimedRewards.aggregatedCoins}
                  aggregatedPiggyBanks={allUnclaimedRewards.aggregatedPiggyBanks}
                  setShowBackgroundPigsAnimation={setShowBackgroundPigsAnimation}
                />
              )}
            </Container>
          )}
          {userQuests &&
            Object.values(QUEST_TYPE).map((value) => (
              <Items
                key={value}
                type={value}
                quests={userQuests[value]}
                setShowBackgroundPigsAnimation={setShowBackgroundPigsAnimation}
              />
            ))}
          {(history?.length > 0 || !questsFound) && (
            <div className="questsListFooter">
              {!questsFound && <NoItems />}
              {questsHistory && history?.length > 0 && (
                <>
                  <RewardHistory rewards={history} type={REWARD_HISTORY_TYPE.QUESTS} />
                  <Paginator hasNextPage={questsHistory?.hasNextPage} loadMore={refetchHistory} />
                </>
              )}
            </div>
          )}
        </Container>
      </SlideLayout>
    </Layout>
  )
}

const styles = {
  container: {
    padding: '0 0 20px',
    'h1, h2, h3, h4, h5, p, span': {
      fontFamily: 'var(--poppins-font)',
    },
    '.questsListItemCompletedDate': {
      fontFamily: 'var(--poppins-font)',
      color: '#27272A',
      margin: 'auto 0',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    height: '100%',
    minHeight: 'calc(100svh - 150px)',
    '@media (min-width: 640px)': {
      gap: '16px',
    },
  },
}

export default QuestsList
